// location as an address grid: street (with Google Places suggestions),
// then apt + city, then state + ZIP — everything editable by hand, with
// autocomplete as a convenience on top. Writes the same object shape as v1
// — {address_line_1, address_line_2, city, state, zip} plus the combined
// `street` and the `no_unit_confirmed` flag — under the field's ref, with
// the state as its two-letter abbreviation. Saves on blur / on selection.
//
// Every part carries its HTML autocomplete token. A phone's contact
// autofill classifies inputs by those tokens; without them it guesses from
// labels and has written the street into the apartment field. The state is
// a select, which an autofill may never reach, so an invisible text input
// stands in front of it to receive the state and hand it to the select.
//
// Like v1, once the address is complete Google Address Validation is asked
// whether it likely needs an apartment/unit number; if so the customer
// must add one or confirm there isn't one before the screen advances.

import React, { useContext, useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox } from "react-aria-components";

import { FormReadOnlyContext } from "../read-only-context";
import { CheckIcon } from "@heroicons/react/24/outline";

// @ts-ignore — svg import, matching the v1 LocationField's attribution.
import GoogleLogo from "svg/google-logo.svg";
import { addressKey, checkAddressNeedsUnit } from "helpers/addressValidation";

import {
  FieldError,
  FieldHelper,
  FieldLabel,
  InputSpinner,
  inputClass,
} from "./chrome";
import SelectInput from "./SelectInput";
import {
  usePlacesAutocomplete,
  type ParsedAddress,
} from "./usePlacesAutocomplete";
import { V2FieldProps } from "./types";

/**
 * Two-letter code → name. The select offers the codes; the names let a
 * value that arrives as a full state name — an autofill, an older answer —
 * snap to its code. The stored value is always the code.
 */
const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const US_STATES = Object.keys(US_STATE_NAMES).map((code) => ({
  value: code,
  label: code,
}));

/**
 * The two-letter code for whatever names a state — the code itself in any
 * case, or the full name as an autofill or an older answer may carry it.
 * Empty when it names no US state.
 */
function stateAbbreviation(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  const code = trimmed.toUpperCase();
  if (code in US_STATE_NAMES) return code;
  const byName = Object.entries(US_STATE_NAMES).find(
    ([, name]) => name.toLowerCase() === trimmed.toLowerCase(),
  );
  return byName ? byName[0] : "";
}

/** The same street entered twice — what a mis-classified autofill produces. */
function repeatsStreet(line1: unknown, line2: unknown): boolean {
  const normalize = (part: unknown) =>
    String(part ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  const first = normalize(line1);
  return first !== "" && first === normalize(line2);
}

const EMPTY_ADDRESS_KEY = addressKey({
  addressLine1: "",
  city: "",
  state: "",
  zip: "",
});

interface TextPartProps {
  name: string;
  label: string;
  required: boolean;
  requiredMessage: string;
  span: string;
  autoComplete: string;
  onCommit: () => void;
}

function TextPart({
  name,
  label,
  required,
  requiredMessage,
  span,
  autoComplete,
  onCommit,
}: TextPartProps) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={{
        validate: (value) => {
          if (!required) return true;
          return String(value ?? "").trim() !== "" || requiredMessage;
        },
      }}
      render={({ field: rhf, fieldState: { error } }) => (
        <div className={span}>
          <FieldLabel text={label} htmlFor={name} optional={!required} />
          <input
            id={name}
            name={rhf.name}
            ref={rhf.ref}
            type="text"
            autoComplete={autoComplete}
            value={rhf.value ?? ""}
            className={inputClass(Boolean(error))}
            onChange={rhf.onChange}
            onBlur={() => {
              rhf.onBlur();
              onCommit();
            }}
          />
          <FieldError message={error?.message} />
        </div>
      )}
    />
  );
}

interface AutofillTargetProps {
  autoComplete: string;
  name: string;
  /** Receives what an autofill wrote, trimmed, never empty. */
  onFill: (value: string) => void;
}

/**
 * An invisible text input whose only job is to receive a browser's or a
 * phone's autofill on behalf of a control the autofill cannot reach itself.
 * It lies over that control with no opacity and no pointer events, so a
 * person never sees or reaches it, and it must come before the control in
 * the DOM: Safari fills the first candidate it finds for a token. It listens
 * on the DOM, not through React — Safari writes the value from script, which
 * React's change tracking cannot tell from a render — and the value is
 * cleared once handed on, so the next autofill is a fresh fill rather than
 * a skipped prefilled field.
 */
function AutofillTarget({ autoComplete, name, onFill }: AutofillTargetProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onFillRef = useRef(onFill);
  useEffect(() => {
    onFillRef.current = onFill;
  }, [onFill]);
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;
    const handOn = () => {
      const value = input.value.trim();
      if (value === "") return;
      input.value = "";
      onFillRef.current(value);
    };
    // animationstart comes from the autofill keyframe in index.css, for an
    // autofill that writes the value without an input or change event.
    const events = ["input", "change", "animationstart"];
    for (const event of events) input.addEventListener(event, handOn);
    return () => {
      for (const event of events) input.removeEventListener(event, handOn);
    };
  }, []);
  return (
    <input
      ref={inputRef}
      type="text"
      name={name}
      autoComplete={autoComplete}
      tabIndex={-1}
      aria-hidden="true"
      className="autofill-target pointer-events-none absolute inset-0 h-full w-full opacity-0"
    />
  );
}

export default function AddressField({ field, onCommit }: V2FieldProps) {
  // react-aria presses run on pointer events, which fire even inside a
  // disabled fieldset — record mode must disable the checkbox by hand.
  const readOnly = useContext(FormReadOnlyContext);
  const { control, getValues, setValue, watch, trigger } = useFormContext();
  const { suggestions, onStreetInput, selectSuggestion, dismiss } =
    usePlacesAutocomplete();
  const streetBlockRef = useRef<HTMLDivElement | null>(null);

  const streetName = `${field.ref}.address_line_1`;
  const aptName = `${field.ref}.address_line_2`;
  const stateName = `${field.ref}.state`;
  const noUnitName = `${field.ref}.no_unit_confirmed`;

  const line1Value = watch(streetName);
  const line2Value = watch(aptName);
  const cityValue = watch(`${field.ref}.city`);
  const stateValue = watch(stateName);
  const zipValue = watch(`${field.ref}.zip`);

  const [unitCheck, setUnitCheck] = useState<"idle" | "unit_missing">("idle");
  const [checkingUnit, setCheckingUnit] = useState(false);
  // The background check (and its spinner) waits until focus leaves the
  // address block — typing a street must not flash a spinner two fields
  // down. Continue never waits on this: its validator runs the check
  // itself.
  const [addressFocused, setAddressFocused] = useState(false);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const lastCheckedKeyRef = useRef<string | null>(null);

  // Ask Google Address Validation whether the address likely needs an
  // apartment/unit number, once the address is complete and Line 2 is
  // empty — v1's behavior, ported.
  useEffect(() => {
    const zip = zipValue || "";
    const key = addressKey({
      addressLine1: line1Value || "",
      city: cityValue || "",
      state: stateValue || "",
      zip,
    });

    if (lastCheckedKeyRef.current === null) {
      // First run may carry restored values — keep a restored confirmation.
      lastCheckedKeyRef.current = key;
    } else if (key !== lastCheckedKeyRef.current) {
      // A change away from the empty key is hydration, not a customer edit —
      // keep a restored confirmation in that case.
      const wasEmpty = lastCheckedKeyRef.current === EMPTY_ADDRESS_KEY;
      lastCheckedKeyRef.current = key;
      setUnitCheck("idle");
      if (!wasEmpty && getValues(noUnitName)) {
        setValue(noUnitName, false);
      }
    }

    const isComplete =
      Boolean(line1Value && cityValue && stateValue) && /^\d{5}$/.test(zip);
    if (!isComplete || line2Value || addressFocused) {
      setCheckingUnit(false);
      return undefined;
    }

    setCheckingUnit(true);
    const timer = window.setTimeout(async () => {
      const result = await checkAddressNeedsUnit({
        addressLine1: line1Value,
        city: cityValue,
        state: stateValue,
        zip,
      });
      if (lastCheckedKeyRef.current === key) {
        setCheckingUnit(false);
        if (result === "unit_missing") {
          setUnitCheck("unit_missing");
        }
      }
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [
    line1Value,
    line2Value,
    cityValue,
    stateValue,
    zipValue,
    addressFocused,
    getValues,
    setValue,
    noUnitName,
  ]);

  // An autofill that wrote the street into the apartment line is undone the
  // moment it lands, before any commit reads the two lines.
  useEffect(() => {
    if (repeatsStreet(line1Value, line2Value)) setValue(aptName, "");
  }, [line1Value, line2Value, aptName, setValue]);

  const showUnitPrompt = unitCheck === "unit_missing" && !line2Value;

  /**
   * The authoritative gate, run by trigger() on Continue: awaits the unit
   * check itself (cached after the first call for an address), so typing a
   * complete address and immediately clicking Continue still waits for the
   * verdict instead of slipping past a debounce.
   */
  const validateUnitGate = async (): Promise<string | true> => {
    if (String(getValues(aptName) ?? "").trim() !== "") return true;
    if (getValues(noUnitName)) return true;
    const line1 = getValues(streetName) || "";
    const city = getValues(`${field.ref}.city`) || "";
    const state = getValues(stateName) || "";
    const zip = getValues(`${field.ref}.zip`) || "";
    const isComplete = Boolean(line1 && city && state) && /^\d{5}$/.test(zip);
    if (!isComplete) return true;
    const result = await checkAddressNeedsUnit({
      addressLine1: line1,
      city,
      state,
      zip,
    });
    if (result !== "unit_missing") return true;
    setUnitCheck("unit_missing");
    return "Add the apt/unit number, or confirm there isn't one.";
  };

  // v1 keeps a combined street alongside the lines; downstream reads it.
  const commitWithStreet = () => {
    const line1 = getValues(streetName);
    let line2 = getValues(aptName);
    if (repeatsStreet(line1, line2)) {
      line2 = "";
      setValue(aptName, "");
    }
    setValue(`${field.ref}.street`, [line1, line2].filter(Boolean).join(" "));
    onCommit();
  };

  /** Whatever names the state — a pick or an autofill, code or full name — lands as the code. */
  const applyState = (value: string | null) => {
    setValue(stateName, stateAbbreviation(value), {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCommit();
  };

  const applyParsedAddress = (parsed: ParsedAddress) => {
    const parts: [keyof ParsedAddress, string][] = [
      ["address_line_1", streetName],
      ["address_line_2", aptName],
      ["city", `${field.ref}.city`],
      ["state", stateName],
      ["zip", `${field.ref}.zip`],
    ];
    for (const [key, name] of parts) {
      const value =
        key === "state" ? stateAbbreviation(parsed.state) : parsed[key];
      setValue(name, value ?? "", { shouldValidate: true });
    }
    // A picked address ends the street entry. With focus still in the
    // street input (a pointer pick), blurring it runs the input's own blur
    // handler, which commits; on a phone the keyboard goes down too. A
    // keyboard pick unmounted the focused button, and browsers fire no blur
    // for removed elements, so that path commits here. Either way the block
    // counts as left, so the background unit check can arm.
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      streetBlockRef.current?.contains(active)
    ) {
      active.blur();
    } else {
      commitWithStreet();
    }
    setAddressFocused(false);
  };

  return (
    <div>
      <div
        ref={gridRef}
        // Dense flow at sm lets City backfill the columns beside Apt when
        // the full-width unit prompt sits between them in source order —
        // without it the prompt wraps first and drags City to its own row.
        className="grid grid-cols-1 gap-3 2xs:grid-cols-2 sm:grid-flow-row-dense sm:grid-cols-6"
        onFocusCapture={() => setAddressFocused(true)}
        onBlurCapture={(event) => {
          const next = event.relatedTarget as Node | null;
          if (!next || !gridRef.current?.contains(next)) {
            setAddressFocused(false);
          }
        }}
      >
        <Controller
          control={control}
          name={streetName}
          rules={{
            validate: (value) => {
              if (!field.required) return true;
              return (
                String(value ?? "").trim() !== "" ||
                "Street address is required"
              );
            },
          }}
          render={({ field: rhf, fieldState: { error } }) => (
            <div
              className="relative 2xs:col-span-2 sm:col-span-6"
              ref={streetBlockRef}
            >
              <FieldLabel
                text="Street address"
                htmlFor={streetName}
                optional={!field.required}
              />
              <input
                id={streetName}
                name={rhf.name}
                ref={rhf.ref}
                type="text"
                autoComplete="address-line1"
                value={rhf.value ?? ""}
                className={inputClass(Boolean(error))}
                onChange={(event) => {
                  rhf.onChange(event.target.value);
                  onStreetInput(event.target.value);
                }}
                onBlur={(event) => {
                  // A click on a suggestion must win over the blur dismiss.
                  const next = event.relatedTarget as Node | null;
                  if (next && streetBlockRef.current?.contains(next)) return;
                  dismiss();
                  rhf.onBlur();
                  commitWithStreet();
                }}
              />
              {suggestions.length > 0 && (
                <div
                  className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-lg border border-oat-200 bg-white shadow-lg"
                  // Pressing a suggestion must leave focus in the street
                  // input. Safari (every iPhone) and Firefox never focus a
                  // button on click, so the blur above would see no
                  // relatedTarget and close the list before the click lands.
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.placeId}
                      type="button"
                      className="block w-full px-4 py-2.5 text-left text-soil hover:bg-oat-50 focus:bg-oat-50 focus:outline-none"
                      onClick={() =>
                        selectSuggestion(suggestion.placeId, applyParsedAddress)
                      }
                    >
                      {suggestion.description}
                    </button>
                  ))}
                  <div className="flex items-center justify-end gap-1.5 border-t border-oat-100 px-4 py-1.5 text-[11px] text-oat-700">
                    powered by
                    <span className="w-12">
                      <GoogleLogo />
                    </span>
                  </div>
                </div>
              )}
              <FieldError message={error?.message} />
            </div>
          )}
        />
        <Controller
          control={control}
          name={aptName}
          rules={{ validate: validateUnitGate }}
          render={({ field: rhf, fieldState: { error, isValidating } }) => (
            <div className="2xs:col-span-2 sm:col-span-2">
              <FieldLabel text="Apt, suite, unit" htmlFor={aptName} optional />
              <div className="relative">
                <input
                  id={aptName}
                  name={rhf.name}
                  ref={rhf.ref}
                  type="text"
                  autoComplete="address-line2"
                  value={rhf.value ?? ""}
                  className={inputClass(Boolean(error))}
                  onChange={rhf.onChange}
                  onBlur={() => {
                    rhf.onBlur();
                    commitWithStreet();
                  }}
                />
                <InputSpinner show={checkingUnit || isValidating} />
              </div>
              <FieldError message={error?.message} />
            </div>
          )}
        />
        {/* Directly after the apt cell on purpose: at 2xs every cell is a
            full-width row, so source order is visual order — the prompt must
            sit under the input it talks about. At sm the dense grid flow
            keeps City beside Apt and drops the prompt to the row below. */}
        {showUnitPrompt && (
          <div className="2xs:col-span-2 sm:col-span-6">
            <Controller
              name={noUnitName}
              control={control}
              defaultValue={false}
              render={({ field: noUnitField }) => (
                <Checkbox
                  isDisabled={readOnly}
                  isSelected={Boolean(noUnitField.value)}
                  onChange={(isSelected) => {
                    noUnitField.onChange(isSelected);
                    // The apt error must clear the moment this is confirmed.
                    void trigger(aptName);
                    commitWithStreet();
                  }}
                  className="flex cursor-pointer items-center gap-2 text-sm text-soil"
                >
                  {({ isSelected }) => (
                    <>
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? "border-evergreen bg-evergreen"
                            : "border-oat-300 bg-white"
                        }`}
                      >
                        {isSelected && (
                          <CheckIcon className="h-4 w-4 text-white" />
                        )}
                      </span>
                      There is no apt/unit number
                    </>
                  )}
                </Checkbox>
              )}
            />
          </div>
        )}
        <TextPart
          name={`${field.ref}.city`}
          label="City"
          required={field.required}
          requiredMessage="City is required"
          span="2xs:col-span-2 sm:col-span-4"
          autoComplete="address-level2"
          onCommit={commitWithStreet}
        />
        <Controller
          control={control}
          name={stateName}
          rules={{
            required: field.required ? "State is required" : false,
          }}
          render={({ field: rhf, fieldState: { error } }) => (
            <div className="sm:col-span-3">
              <FieldLabel
                text="State"
                htmlFor={stateName}
                optional={!field.required}
              />
              <div className="relative">
                <AutofillTarget
                  autoComplete="address-level1"
                  name="state"
                  onFill={applyState}
                />
                <SelectInput
                  id={stateName}
                  ariaLabel="State"
                  options={US_STATES}
                  value={stateAbbreviation(rhf.value) || null}
                  invalid={Boolean(error)}
                  onChange={applyState}
                  onBlur={rhf.onBlur}
                />
              </div>
              <FieldError message={error?.message} />
            </div>
          )}
        />
        <TextPart
          name={`${field.ref}.zip`}
          label="ZIP"
          required={field.required}
          requiredMessage="ZIP is required"
          span="sm:col-span-3"
          autoComplete="postal-code"
          onCommit={commitWithStreet}
        />
      </div>
      <FieldHelper text={field.description} />
    </div>
  );
}
